import useResizeObserver from '@react-hook/resize-observer';
import { HierarchyNode } from 'd3';
import {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState
} from 'react';
import { useNavigate } from 'react-router';

import fetchWithRetry from 'services/fetch-retry';
import { EquipmentType } from 'types';
import { Filter, FilterOperator, FilterState, LogicalOperator } from 'utils';

import { Loader } from 'components';
import { useSizeTypes } from 'contexts';
import { SelectedLocationContext } from 'contexts/selected-location';
import { ChartComponentProps } from '../chart-card';
import TreeMap, { TreeMapData } from './tree-map';

class SizeTypeData {
  data: TreeMapData = { name: 'SIZE TYPES', children: [] };
  loading: boolean = true;
}

const SizeTypeDataContext = createContext(new SizeTypeData());

export const SizeTypeDataProvider: FC<PropsWithChildren> = children => {
  const { selectedLocation } = useContext(SelectedLocationContext);
  const { getEquipmentType, isReefer } = useSizeTypes();

  const [counts, setCounts] = useState<{ sizeType: string; unitCount: number }[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const getCounts = useCallback(async () => {
    setLoading(true);
    const url = new URL(`/api/units/counts/size-type?location=${selectedLocation?.id}`, window.location.origin);
    setCounts(await fetchWithRetry<typeof counts>(url, { method: 'GET' }, []));
    setLoading(false);
  }, [selectedLocation?.id]);

  useEffect(() => {
    getCounts();
  }, [getCounts]);

  const data: TreeMapData = useMemo(() => {
    const children = counts.reduce(
      (acc: TreeMapData[], { sizeType, unitCount }) => {
        const equipmentType = getEquipmentType(sizeType);
        if (equipmentType === EquipmentType.CHASSIS) {
          acc[0].children!.push({ category: 'CHASSIS', name: sizeType, value: unitCount });
        } else if (equipmentType === EquipmentType.CONTAINER) {
          if (!isReefer(sizeType)) {
            acc[1].children![0]!.children!.push({ category: 'DRY', name: sizeType, value: unitCount });
          } else {
            acc[1].children![1]!.children!.push({ category: 'REEFER', name: sizeType, value: unitCount });
          }
        } else if (equipmentType === EquipmentType.GENSET) {
          acc[2].children!.push({ category: 'GENSET', name: sizeType, value: unitCount });
        }
        return acc;
      },
      [
        { name: 'CHASSIS', children: [], colorScheme: ['#366375', '#4E8392', '#7BA2AD', '#A7C1C8', '#D3E0E3'] },
        {
          name: 'CONTAINER',
          children: [
            {
              category: 'CONTAINER',
              name: 'DRY',
              children: [],
              colorScheme: ['#26296B', '#3B3E79', '#515388', '#676997', '#9294B5']
            },
            {
              category: 'CONTAINER',
              name: 'REEFER',
              children: [],
              colorScheme: ['#54266B', '#653B79', '#765188', '#876797', '#987CA6']
            }
          ]
        },
        { name: 'GENSET', children: [], colorScheme: ['#86AC4F', '#96BD64', '#B0CD8B', '#CADEB1', '#E4EED8'] }
      ]
    );
    return { name: 'SIZE TYPES', children };
  }, [counts, getEquipmentType, isReefer]);

  return <SizeTypeDataContext.Provider value={{ data, loading }} {...children} />;
};

const SizeTypeUnits: FC<ChartComponentProps> = ({ boxRef, focused, filter }) => {
  const navigate = useNavigate();

  const { data, loading } = useContext(SizeTypeDataContext);
  const [total, setTotal] = useState(0);

  const [dimensions, setDimensions] = useState({ height: 0, width: 0 });
  useLayoutEffect(() => {
    setDimensions(() => ({
      width: boxRef.current?.clientWidth ?? 0,
      height: boxRef.current?.clientHeight ?? 0
    }));
  }, [boxRef]);
  useResizeObserver(boxRef.current, ({ contentBoxSize }) => {
    setDimensions({
      width: contentBoxSize[0].inlineSize,
      height: contentBoxSize[0].blockSize
    });
  });

  /* include both ungrouped-unit and stack filters, yard-management will apply conditionally based on view */
  const handleClick = useCallback(
    (_: PointerEvent, { data }: HierarchyNode<TreeMapData>) => {
      const filters: Filter[] = [
        { field: 'chassisSizeType', value: data.name, filterOperator: FilterOperator.CONTAINS },
        { field: 'gensetSizeType', value: data.name, filterOperator: FilterOperator.CONTAINS },
        { field: 'sizeType', value: data.name, filterOperator: FilterOperator.CONTAINS }
      ];
      const filterState: FilterState = { logicalOperator: LogicalOperator.OR, filters };
      navigate('/units', { state: { filterState } });
    },
    [navigate]
  );

  const filteredData = useMemo(() => {
    let filteredTotal = 0;
    const fn = (acc: TreeMapData[], c: TreeMapData): TreeMapData[] => {
      if (!c.children) {
        if (!filter || filter(c)) {
          filteredTotal += c.value ?? 0;
          acc.push(c);
        }
      } else {
        acc.push({ ...c, children: c.children?.reduce(fn, []) });
      }
      return acc;
    };
    const children = data.children?.reduce(fn, []) ?? [];
    setTotal(filteredTotal);
    return { name: 'SIZE TYPES', children };
  }, [data, filter, setTotal]);

  return (
    <div className="h-100">
      <Loader loading={loading}>
        <TreeMap data={filteredData} handleClick={handleClick} focused={focused} total={total} {...dimensions} />
      </Loader>
    </div>
  );
};

export default SizeTypeUnits;
